import React, { useState } from 'react';
import { X, Loader2, Phone, PhoneOff, PhoneMissed, PhoneCall } from 'lucide-react';
import { api } from '@/utils/api';

// ─── Canonical 7 stages (for stage-conditional visibility) ───────────────
export type CanonicalStage = 'cold' | 'followup' | 'quote' | 'neg' | 'hot' | 'won' | 'lost';

interface Disposition {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  // event fired to backend
  eventType: string;
  // only shown when lead is in one of these stages (undefined = always shown)
  showInStages?: CanonicalStage[];
  // triggers competitor name input
  needsCompetitorName?: boolean;
  // visual accent
  accent?: 'red' | 'orange' | 'green' | 'default';
}

const DISPOSITIONS: Disposition[] = [
  {
    id: 'connected',
    label: 'Connected — qualifying',
    description: 'Spoke to the customer, gathering requirements',
    icon: <PhoneCall size={14} />,
    eventType: 'call_connected',
    accent: 'green',
  },
  {
    id: 'no_answer',
    label: 'No Answer',
    description: 'Customer did not pick up',
    icon: <PhoneMissed size={14} />,
    eventType: 'call_no_answer',
    accent: 'orange',
  },
  {
    id: 'busy',
    label: 'Busy',
    description: 'Line was busy',
    icon: <PhoneOff size={14} />,
    eventType: 'call_busy',
    accent: 'orange',
  },
  {
    id: 'disconnected',
    label: 'Disconnected',
    description: 'Call dropped or line not reachable',
    icon: <Phone size={14} />,
    eventType: 'call_disconnected',
    accent: 'orange',
  },
  {
    id: 'low_budget',
    label: 'Customer cited budget',
    description: 'Customer says price is too high → Lost (Budget)',
    eventType: 'dispo_low_budget',
    accent: 'red',
  },
  {
    id: 'lost_competitor',
    label: 'Going with a competitor',
    description: 'Customer chose another vendor → Lost (Competitor)',
    eventType: 'dispo_lost_to_competitor',
    needsCompetitorName: true,
    accent: 'red',
  },
  {
    id: 'proposal_accepted',
    label: 'Customer accepted proposal',
    description: 'Proposal accepted → advances to Hot',
    eventType: 'proposal_accepted',
    showInStages: ['quote', 'neg'],
    accent: 'green',
  },
  {
    id: 'counter_offer',
    label: 'Mark as Negotiation',
    description: 'Customer countered — enter negotiation stage',
    eventType: 'customer_counter_offer',
    showInStages: ['quote'],
    accent: 'default',
  },
];

interface DispositionPickerModalProps {
  leadId: string;
  leadName?: string;
  canonicalStage?: CanonicalStage | string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ACCENT_STYLES: Record<string, React.CSSProperties> = {
  green: { background: 'var(--forest-50, #EAF2EC)', color: 'var(--forest-600, #24604A)', borderColor: 'var(--forest-100, #D6E7DC)' },
  red: { background: 'rgba(138,42,58,0.08)', color: '#8A2A3A', borderColor: 'rgba(138,42,58,0.2)' },
  orange: { background: 'var(--saffron-50, #FBF3DF)', color: 'var(--saffron-600, #B87A2E)', borderColor: 'var(--saffron-100, #F5E7C9)' },
  default: { background: 'var(--ivory-2, #F4F0E4)', color: 'var(--ink-1, #3A332B)', borderColor: 'var(--ivory-3, #EBE6D6)' },
};

export function DispositionPickerModal({
  leadId,
  leadName,
  canonicalStage,
  onClose,
  onSuccess,
}: DispositionPickerModalProps) {
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [competitorName, setCompetitorName] = useState('');
  const [showCompetitorInput, setShowCompetitorInput] = useState(false);
  const [pendingDisposition, setPendingDisposition] = useState<Disposition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const visibleDispositions = DISPOSITIONS.filter((d) => {
    if (!d.showInStages) return true;
    return canonicalStage && d.showInStages.includes(canonicalStage as CanonicalStage);
  });

  const fireDisposition = async (dispo: Disposition, extraPayload?: Record<string, string>) => {
    setSubmitting(dispo.id);
    setError(null);
    try {
      await api.post('/telecaller/calls', {
        leadImportId: leadId,
        disposition: dispo.eventType,
        eventType: dispo.eventType,
        ...extraPayload,
      });
      setSuccess(`Logged: ${dispo.label}`);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 900);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to log disposition. Try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const handleSelect = (dispo: Disposition) => {
    if (dispo.needsCompetitorName) {
      setPendingDisposition(dispo);
      setShowCompetitorInput(true);
      return;
    }
    fireDisposition(dispo);
  };

  const handleCompetitorSubmit = () => {
    if (!pendingDisposition) return;
    if (!competitorName.trim()) {
      setError('Enter the competitor name to continue.');
      return;
    }
    fireDisposition(pendingDisposition, { competitorName: competitorName.trim() });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(27,23,19,0.55)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--ivory-1, #FAF7EF)',
          border: '1px solid var(--line, #E7E0CF)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          padding: '24px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--ink-0, #1B1713)' }}>
              Log Call Outcome
            </h3>
            {leadName && (
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--ink-3, #8B8373)' }}>
                {leadName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3, #8B8373)',
              padding: '2px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Success state */}
        {success && (
          <div style={{ background: 'var(--forest-50, #EAF2EC)', color: 'var(--forest-600, #24604A)', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
            {success}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div style={{ background: 'rgba(138,42,58,0.08)', color: '#8A2A3A', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
            {error}
          </div>
        )}

        {/* Competitor name input (only shown when user picks "going with competitor") */}
        {showCompetitorInput ? (
          <div>
            <p style={{ fontSize: '13px', color: 'var(--ink-1, #3A332B)', marginBottom: '10px' }}>
              Which competitor?
            </p>
            <input
              autoFocus
              type="text"
              placeholder="e.g. ABC Industries, XYZ Traders…"
              value={competitorName}
              onChange={(e) => setCompetitorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCompetitorSubmit()}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '9px 12px',
                borderRadius: '8px',
                border: '1.5px solid var(--line, #E7E0CF)',
                background: 'var(--ivory-0, #FEFCF7)',
                color: 'var(--ink-0, #1B1713)',
                fontSize: '13px',
                outline: 'none',
                marginBottom: '12px',
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setShowCompetitorInput(false); setPendingDisposition(null); setCompetitorName(''); }}
                style={{
                  flex: 1,
                  padding: '9px',
                  borderRadius: '8px',
                  border: '1.5px solid var(--line, #E7E0CF)',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--ink-2, #5E564B)',
                }}
              >
                Back
              </button>
              <button
                onClick={handleCompetitorSubmit}
                disabled={!!submitting}
                style={{
                  flex: 2,
                  padding: '9px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#8A2A3A',
                  color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                Confirm Lost — Competitor
              </button>
            </div>
          </div>
        ) : (
          /* Disposition list */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {visibleDispositions.map((dispo) => {
              const accentStyle = ACCENT_STYLES[dispo.accent ?? 'default'];
              const isLoading = submitting === dispo.id;
              return (
                <button
                  key={dispo.id}
                  onClick={() => handleSelect(dispo)}
                  disabled={!!submitting}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border: `1.5px solid ${accentStyle.borderColor ?? 'var(--line)'}`,
                    background: accentStyle.background,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    opacity: submitting && !isLoading ? 0.6 : 1,
                    transition: 'opacity 150ms, transform 100ms',
                    transform: isLoading ? 'scale(0.98)' : 'scale(1)',
                  }}
                >
                  {isLoading ? (
                    <Loader2 size={14} style={{ marginTop: '1px', flexShrink: 0, color: accentStyle.color, animation: 'spin 1s linear infinite' }} />
                  ) : dispo.icon ? (
                    <span style={{ marginTop: '1px', flexShrink: 0, color: accentStyle.color }}>
                      {dispo.icon}
                    </span>
                  ) : (
                    <span style={{ width: '14px', flexShrink: 0 }} />
                  )}
                  <div>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: accentStyle.color }}>
                      {dispo.label}
                    </p>
                    {dispo.description && (
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--ink-3, #8B8373)', lineHeight: 1.4 }}>
                        {dispo.description}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <p
          style={{
            marginTop: '16px',
            fontSize: '10px',
            color: 'var(--ink-3, #8B8373)',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Stages advance via call outcomes and proposal events only.
        </p>
      </div>
    </div>
  );
}
